import * as React from 'react';
import { API_ETH_MOCK_ADDRESS } from '@yldr/contract-helpers';
import { USD_DECIMALS, valueToBigNumber } from '@yldr/math-utils';
import { Trans } from '@lingui/macro';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import BigNumber from 'bignumber.js';
import { Fragment, useState } from 'react';
import { ListColumn } from 'src/components/lists/ListColumn';
import { ListHeaderTitle } from 'src/components/lists/ListHeaderTitle';
import { ListHeaderWrapper } from 'src/components/lists/ListHeaderWrapper';
import { AssetCapsProvider } from 'src/hooks/useAssetCaps';
import { useRootStore } from 'src/store/root';
import { fetchIconSymbolAndName } from 'src/ui-config/reservePatches';

import { EDisplayMode, ListWrapper } from '../../../../components/lists/ListWrapper';
import {
  ComputedReserveData,
  useAppDataContext,
} from '../../../../hooks/app-data-provider/useAppDataProvider';
import { useWalletBalances } from '../../../../hooks/app-data-provider/useWalletBalances';
import {
  DASHBOARD_LIST_COLUMN_WIDTHS,
  DashboardReserve,
  handleSortDashboardReserves,
} from '../../../../utils/dashboardSortUtils';
import { usePositionsDataQuery } from '../../../../hooks/yldr/usePositionsDataQuery';
import { selectEnrichedUniswapPositions } from '../../../../store/positionSelectors';
import { PositionsList } from '../../../../components/PositionsList/PositionsList';
import { SupplyActions } from '../../../../components/PositionsList/components/UniswapPositionsActions/SupplyActions';
import { DashboardContentNoData } from '../../DashboardContentNoData';
import { DashboardListTopPanel } from '../../DashboardListTopPanel';
import { ListButtonsColumn } from '../ListButtonsColumn';
import { ListLoader } from '../ListLoader';
import { SupplyAssetsListItem } from './SupplyAssetsListItem';
import { SupplyAssetsListMobileItem } from './SupplyAssetsListMobileItem';

const head = [
  { title: <Trans key="assets">Assets</Trans>, sortKey: 'symbol' },
  { title: <Trans key="Wallet balance">Balance</Trans>, sortKey: 'walletBalance' },
  { title: <Trans key="APY">APY</Trans>, sortKey: 'supplyAPY' },
];

const positionsHead = [
  {
    title: <Trans>Pool</Trans>,
    sortKey: 'pool',
  },
  {
    title: <Trans key="Liquidity">Liquidity</Trans>,
    sortKey: 'liquidity',
  },
  {
    title: <Trans key="feesEarned">Fees Earned</Trans>,
    sortKey: 'feesEarned',
  },
];

export const SupplyAssetsList = () => {
  const currentNetworkConfig = useRootStore((store) => store.currentNetworkConfig);
  const currentMarketData = useRootStore((store) => store.currentMarketData);
  const { breakpoints } = useTheme();
  const md = useMediaQuery(breakpoints.up('md'));
  const {
    reserves,
    marketReferencePriceInUsd,
    loading: loadingReserves,
  } = useAppDataContext();

  const { data: positionsData, isLoading: isLoadingPositions } = usePositionsDataQuery();

  const enrichedPositionsData = useRootStore((state) => selectEnrichedUniswapPositions(state, positionsData));
  const filteredEnrichedPositionData = enrichedPositionsData.filter(
    (item) => item.marketReferenceCurrencyValue > 0
  );

  const { walletBalances, loading } = useWalletBalances(currentMarketData);
  const theme = useTheme();
  const downToXSM = useMediaQuery(theme.breakpoints.down('xsm'));

  const [sortName, setSortName] = useState('');
  const [sortDesc, setSortDesc] = useState(false);
  const [displayMode, setDisplayMode] = useState(EDisplayMode.Assets);

  const { bridge, baseAssetSymbol } = currentNetworkConfig;

  const localStorageName = 'showSupplyZeroAssets';
  const [hideZeroAssets, setHideZeroAssets] = useState(
    localStorage.getItem(localStorageName) === 'true'
  );

  const tokensToSupply = reserves
    .filter(
      (reserve: ComputedReserveData) =>
        !(reserve.isFrozen || reserve.isPaused)
    )
    .map((reserve: ComputedReserveData) => {
      const walletBalance = walletBalances[reserve.underlyingAsset]?.amount;
      const walletBalanceUSD = walletBalances[reserve.underlyingAsset]?.amountUSD;
      let availableToDeposit = valueToBigNumber(walletBalance);
      if (reserve.supplyCap !== '0') {
        availableToDeposit = BigNumber.min(
          availableToDeposit,
          new BigNumber(reserve.supplyCap).minus(reserve.totalLiquidity).multipliedBy('0.995')
        );
      }
      const availableToDepositUSD = valueToBigNumber(availableToDeposit)
        .multipliedBy(reserve.priceInMarketReferenceCurrency)
        .multipliedBy(marketReferencePriceInUsd)
        .shiftedBy(-USD_DECIMALS)
        .toString();

      const usageAsCollateralEnabledOnUser = reserve.reserveLiquidationThreshold !== '0';

      if (reserve.isWrappedBaseAsset) {
        let baseAvailableToDeposit = valueToBigNumber(
          walletBalances[API_ETH_MOCK_ADDRESS.toLowerCase()]?.amount
        );
        if (reserve.supplyCap !== '0') {
          baseAvailableToDeposit = BigNumber.min(
            baseAvailableToDeposit,
            new BigNumber(reserve.supplyCap).minus(reserve.totalLiquidity).multipliedBy('0.995')
          );
        }
        const baseAvailableToDepositUSD = valueToBigNumber(baseAvailableToDeposit)
          .multipliedBy(reserve.priceInMarketReferenceCurrency)
          .multipliedBy(marketReferencePriceInUsd)
          .shiftedBy(-USD_DECIMALS)
          .toString();
        return [
          {
            ...reserve,
            reserve,
            underlyingAsset: API_ETH_MOCK_ADDRESS.toLowerCase(),
            ...fetchIconSymbolAndName({
              symbol: baseAssetSymbol,
              underlyingAsset: API_ETH_MOCK_ADDRESS.toLowerCase(),
            }),
            walletBalance: walletBalances[API_ETH_MOCK_ADDRESS.toLowerCase()]?.amount,
            walletBalanceUSD: walletBalances[API_ETH_MOCK_ADDRESS.toLowerCase()]?.amountUSD,
            availableToDeposit: baseAvailableToDeposit.toString(),
            availableToDepositUSD: baseAvailableToDepositUSD,
            usageAsCollateralEnabledOnUser,
            detailsAddress: reserve.underlyingAsset,
            id: reserve.id + 'base',
          },
          {
            ...reserve,
            reserve,
            walletBalance,
            walletBalanceUSD,
            availableToDeposit:
              availableToDeposit.toNumber() <= 0 ? '0' : availableToDeposit.toString(),
            availableToDepositUSD:
              Number(availableToDepositUSD) <= 0 ? '0' : availableToDepositUSD.toString(),
            usageAsCollateralEnabledOnUser,
            detailsAddress: reserve.underlyingAsset,
          },
        ];
      }

      return {
        ...reserve,
        reserve,
        walletBalance,
        walletBalanceUSD,
        availableToDeposit:
          availableToDeposit.toNumber() <= 0 ? '0' : availableToDeposit.toString(),
        availableToDepositUSD:
          Number(availableToDepositUSD) <= 0 ? '0' : availableToDepositUSD.toString(),
        usageAsCollateralEnabledOnUser,
        detailsAddress: reserve.underlyingAsset,
      };
    })
    .flat();

  const sortedSupplyReserves = tokensToSupply.sort((a, b) =>
    +a.walletBalanceUSD > +b.walletBalanceUSD ? -1 : 1
  );
  const filteredSupplyReserves = sortedSupplyReserves.filter(
    (reserve) => reserve.availableToDepositUSD !== '0'
  );

  // Filter out reserves
  const supplyReserves: unknown = !hideZeroAssets
    ? sortedSupplyReserves
    : filteredSupplyReserves.length >= 1
    ? filteredSupplyReserves
    : sortedSupplyReserves;

  // Transform to the DashboardReserve schema so the sort helpers can work with it
  const preSortedReserves = supplyReserves as DashboardReserve[];
  const sortedReserves = handleSortDashboardReserves(
    sortDesc,
    sortName,
    'assets',
    preSortedReserves
  );

  const supplyDisabled = !tokensToSupply.length;

  if (loadingReserves || loading || isLoadingPositions) {
    return (
      <ListLoader
        head={head.map((col) => col.title)}
        title={<Trans>Assets to supply</Trans>}
        withTopMargin
      />
    );
  }

  return (
    <ListWrapper
      titleComponent={
        <Typography component="div" variant="h3" sx={{ mr: 4 }}>
          <Trans>Assets to supply</Trans>
        </Typography>
      }
      displayMode={displayMode}
      onSwitchDisplayMode={setDisplayMode}
      hasCollapseButton={!md}
      localStorageName="supplyAssetsDashboardTableCollapse"
      withTopMargin
      noData={supplyDisabled}
      subChildrenComponent={
        <>
          {filteredSupplyReserves.length >= 1 && (
            <DashboardListTopPanel
              value={hideZeroAssets}
              onClick={setHideZeroAssets}
              localStorageName={localStorageName}
              bridge={bridge}
            />
          )}
        </>
      }
    >
      {displayMode === EDisplayMode.Assets && (
        <>
          {sortedReserves.length && !downToXSM && !!sortedReserves && !supplyDisabled && (
            <ListHeaderWrapper>
              {head.map((col) => (
                <ListColumn
                  isRow={col.sortKey === 'symbol'}
                  maxWidth={col.sortKey === 'symbol' ? DASHBOARD_LIST_COLUMN_WIDTHS.ASSET : undefined}
                  key={col.sortKey}
                  overFlow={'visible'}
                >
                  <ListHeaderTitle
                    sortName={sortName}
                    sortDesc={sortDesc}
                    setSortName={setSortName}
                    setSortDesc={setSortDesc}
                    sortKey={col.sortKey}
                  >
                    {col.title}
                  </ListHeaderTitle>
                </ListColumn>
              ))}
              <ListButtonsColumn isColumnHeader />
            </ListHeaderWrapper>
          )}
          {sortedReserves.length ? sortedReserves.map((item) => (
            <Fragment key={item.underlyingAsset}>
              <AssetCapsProvider asset={item.reserve}>
                {downToXSM ? (
                  <SupplyAssetsListMobileItem {...item} key={item.id}/>
                ) : (
                  <SupplyAssetsListItem {...item} key={item.id}/>
                )}
              </AssetCapsProvider>
            </Fragment>
          )) : (
            <DashboardContentNoData text={<Trans>Nothing supplied yet</Trans>} />
          )}
        </>
      )}
        {displayMode === EDisplayMode.Positions && (
          <PositionsList
            head={positionsHead}
            positions={filteredEnrichedPositionData}
            actions={(args) => <SupplyActions tokenId={args.tokenId} />}
            columnsType='lending'
          />
        )}
    </ListWrapper>
  );
};
